import { useEffect, useMemo } from 'react'

import { Box, Button, FormHelperText, Grid, Stack, Tooltip } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'

import { ControlledCheckbox, ControlledTextField, TextInput } from 'components/common'
import {
  DEFAULT_CUSTOMER_WAIT_AND_RETURN_FEE_CATEGORY_ID,
  DEFAULT_WAIT_AND_RETURN_FEE_CATEGORY_ID,
} from 'constants/index'
import { Tariff } from 'generated/graphql'
import usePrevious from 'helpers/usePrevious'
import { emitter } from 'providers'
import { TBookingForm } from 'types/form-types'

import {
  getWaitAndReturnTooltipText,
  isCollectionAndDeliveryAddressPopulated,
} from '../InternalTariff/helpers'

interface IProps {
  tariffDetails?: Tariff
  isQuote: boolean
  isQuickQuote: boolean
  disabled?: boolean
  onSelectTariffClick: () => void
  currentWaitAndReturnFeeCategoryId?: number
  currentIsWaitAndReturn?: boolean
  customerWaitAndReturnFeeCategoryId?: number
}

const ExternalTariffForm = (props: IProps) => {
  const {
    tariffDetails,
    isQuote,
    isQuickQuote,
    disabled,
    onSelectTariffClick,
    currentWaitAndReturnFeeCategoryId,
    currentIsWaitAndReturn,
    customerWaitAndReturnFeeCategoryId,
  } = props

  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<TBookingForm>()

  const [collectionAddresses, deliveryAddresses, isWaitAndReturn, customerId] = useWatch({
    name: ['collectionAddresses', 'deliveryAddresses', 'isWaitAndReturn', 'customerId'],
  })

  useEffect(() => {
    if (!isWaitAndReturn) return

    const defaultId = Number(DEFAULT_WAIT_AND_RETURN_FEE_CATEGORY_ID)

    const resolvedId =
      currentWaitAndReturnFeeCategoryId != null && currentWaitAndReturnFeeCategoryId !== defaultId
        ? String(currentWaitAndReturnFeeCategoryId)
        : customerWaitAndReturnFeeCategoryId != null &&
          customerWaitAndReturnFeeCategoryId !== defaultId
        ? String(customerWaitAndReturnFeeCategoryId)
        : DEFAULT_CUSTOMER_WAIT_AND_RETURN_FEE_CATEGORY_ID

    setValue('waitAndReturnFeeCategoryId', resolvedId, { shouldDirty: false })
  }, [
    isWaitAndReturn,
    currentWaitAndReturnFeeCategoryId,
    customerWaitAndReturnFeeCategoryId,
    setValue,
  ])

  const isWaitAndReturnModified = useMemo(() => {
    return (
      currentIsWaitAndReturn &&
      currentWaitAndReturnFeeCategoryId !== undefined &&
      currentWaitAndReturnFeeCategoryId !== Number(DEFAULT_CUSTOMER_WAIT_AND_RETURN_FEE_CATEGORY_ID)
    )
  }, [currentIsWaitAndReturn, currentWaitAndReturnFeeCategoryId])

  const isWaitAndReturnReadOnly = disabled || isWaitAndReturnModified

  const isWaitAndReturnInvalid = useMemo(() => {
    return (
      !customerId ||
      (collectionAddresses && collectionAddresses.length > 1) ||
      !isCollectionAndDeliveryAddressPopulated(collectionAddresses, deliveryAddresses)
    )
  }, [collectionAddresses, customerId, deliveryAddresses])

  const isWaitAndReturnDisabled = isWaitAndReturnReadOnly || isWaitAndReturnInvalid

  const wasWaitAndReturnInvalid = usePrevious(isWaitAndReturnInvalid)

  useEffect(() => {
    if (isWaitAndReturnInvalid && wasWaitAndReturnInvalid === false) {
      setValue('isWaitAndReturn', false)
    }
  }, [isWaitAndReturnInvalid, wasWaitAndReturnInvalid, setValue])

  useEffect(() => {
    setValue('serviceType', tariffDetails?.xeroCode?.serviceType)
    emitter.emit('xero-service-type-change')
  }, [tariffDetails?.xeroCode?.serviceType, setValue])

  return (
    <Grid container spacing={1.5}>
      <Grid
        item
        xs={12}
        sm={6}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60px' }}>
        {tariffDetails?.tariffIcon && (
          <img
            src={tariffDetails?.tariffIcon?.file?.absoluteUri}
            alt={`${tariffDetails?.tariffIcon?.name} Icon`}
            style={{ maxHeight: '60px' }}
          />
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        {isQuote && (
          <Stack alignItems='center'>
            <Button
              variant='contained'
              color='grey'
              disabled={disabled}
              onClick={onSelectTariffClick}>
              Select Tariff
            </Button>

            {errors.tariffId && (
              <FormHelperText error required focused>
                {errors.tariffId.message}
              </FormHelperText>
            )}

            {/* //* Used to focus on form error */}
            <Box sx={{ width: 0, height: 0, overflow: 'hidden' }}>
              <ControlledTextField name='tariffId' label='Tariff Id' />
            </Box>
          </Stack>
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput label='Name' value={tariffDetails?.name} disabled={disabled} readOnly />
      </Grid>

      {!isQuickQuote && (
        <Grid
          item
          xs={12}
          sm={6}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Tooltip
            title={getWaitAndReturnTooltipText(collectionAddresses, deliveryAddresses, customerId)}
            arrow
            placement='top'>
            <div>
              <ControlledCheckbox
                control={control}
                label='Wait &amp; Return'
                name='isWaitAndReturn'
                defaultValue={false}
                disabled={isWaitAndReturnDisabled}
              />
            </div>
          </Tooltip>
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <TextInput
          label='Category'
          value={tariffDetails?.tariffCategory?.name}
          disabled={disabled}
          readOnly
        />
      </Grid>

      <Grid item xs={12}>
        <TextInput label='Type' value={tariffDetails?.type} disabled={disabled} readOnly />
      </Grid>

      <Grid item xs={12}>
        <TextInput
          label='Vehicle Capacity'
          value={tariffDetails?.tariffIcon?.vehicleCapacity}
          disabled={disabled}
          readOnly
          multiline
        />
      </Grid>
    </Grid>
  )
}

export default ExternalTariffForm
